import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { ProductPromotionDatesComponent } from '../product-promotion-dates/product-promotion-dates.component';
import { ProductUnitPriceComponent } from '../product-unit-price/product-unit-price.component';

import { getCmsConfigValue, getPage } from '@core/infrastructure/state/core.selectors';
import { ProductPublicModels } from '@features/products/product';
import { GoToSection } from '@shared/navigation';
import { FormattedPriceComponent } from '@shared/prices/components/formatted-price/formatted-price.component';
import { PricesClasses, ProductMapPrices, PriceStyles } from '@shared/prices/models/product-prices.model';
import { ProductPriceService } from '@shared/prices/services/product-prices.service';
import { ProductViewConfig } from '@shared/product/models';

@Component({
    selector: 'product-prices',
    templateUrl: './product-prices.component.html',
    imports: [
        ProductPromotionDatesComponent,
        ProductUnitPriceComponent,
        FormattedPriceComponent,
        AsyncPipe,
        NgClass,
        TranslateModule,
        NgStyle,
    ],
    providers: [ ProductPriceService ],
    standalone: true,
})
export class ProductPricesComponent implements OnChanges {
    public openSuscriptionJoin(showSuscriptionCTA: boolean) {
        if (showSuscriptionCTA) {
            const vipConfig = this.store.selectSignal(getCmsConfigValue('vip_config'))();
            const vipLinks = {
                FEEL: 'activeCRM',
                NL: 'joinNL',
            };
            if (vipLinks[vipConfig]) {
                this.store.dispatch(GoToSection({ url: vipLinks[vipConfig] }));
            }
        }
    }

    @Input() public productPrices: ProductMapPrices;

    @Input() public hasPromotion = false;

    @Input() public templateConfiguration?: ProductViewConfig;

    @Input() public showPricesInFlexMode: boolean;

    @Input() public alternativePricesClass?: string;

    @Input() public pricesInTwoColumns = false;

    @Input() public isPageProduct = false;

    @Input() public pricesClasses: PricesClasses;

    @Input() public hidePriceText?: boolean;

    @Input() public product?: ProductPublicModels.PublicProduct;

    @Input() public colorStyle?: string;

    public classPrices$: Observable<PricesClasses>;

    public classPercentPrice$: Observable<string> = this.store.select(getPage).pipe(
        first(),
        map((page: string) => page === 'ItxProductPage' ? 'text-l' : 'text-m')
    );

    public discountText$: Observable<any>;

    public priceStyles$: Observable<PriceStyles>;

    constructor(
        private readonly store: Store,
        private readonly productPriceService: ProductPriceService,
    ) { }

    public ngOnChanges(): void {
        this.classPrices$ = this.productPriceService.getClassPrices(this.productPrices, this.hasPromotion).pipe(
            map((pricesClasses: PricesClasses) => {
                const parentInput = this.pricesClasses?.container ?? '';
                const container = this.showPricesInFlexMode ? `is-flex flex-wrap text-nowrap ${parentInput}` : `${parentInput}`;
                const alternativeCurrencies = this.alternativePricesClass;
                return {
                    ...pricesClasses,
                    ...this.pricesClasses,
                    container,
                    alternativeCurrencies,
                };
            })
        );

        this.priceStyles$ = this.productPriceService.getPriceStyles(this.productPrices, this.hasPromotion);
        this.discountText$ = this.productPriceService.getDiscountText(this.productPrices);
    }
}
